<!-- fallow-ignore-file complexity -->
<mat-form-field
	[appearance]="appearance()"
	floatLabel="always"
	class="jepto-ui-select-field"
	[attr.data-testid]="fieldBoundaryTestId() ?? testId() ?? null"
	[class.jepto-ui-select-field--labeled]="!!label().trim()"
	[class.jepto-ui-select-field--with-prefix]="hasPrefix()"
	subscriptSizing="dynamic"
	[class.has-error]="showError()"
	[class.focused]="focused"
	[class.disabled]="disabled() || triggerLoading()"
	[class.jepto-ui-select-field--trigger-loading]="triggerLoading()"
	[class.multiple]="multiple()"
>
	<!-- Label (+ optional help) -->
	<mat-label *ngIf="label().trim()">
		<span class="jepto-ui-select__label-row">
			<span class="jepto-ui-select__label-text">
				{{ label() }}
				<span class="required-indicator" *ngIf="required()">*</span>
			</span>
			<button
				*ngIf="labelHelpIcon()"
				type="button"
				class="jepto-ui-select__label-help"
				tabindex="0"
				[attr.aria-label]="labelHelpAriaLabel()"
				(click)="onLabelHelpClick($event)"
			></button>
		</span>
	</mat-label>

	<!-- Select Element -->
	<mat-select
		#matSelect="matSelect"
		[id]="controlId"
		[attr.aria-label]="selectAriaLabel() ?? null"
		[attr.data-testid]="testId() ?? null"
		[placeholder]="placeholder()"
		[value]="value"
		[disabled]="disabled() || triggerLoading()"
		[required]="required()"
		[multiple]="multiple()"
		[hideSingleSelectionIndicator]="false"
		[compareWith]="compareWith()"
		[panelClass]="mergedPanelClass()"
		[disableOptionCentering]="disableOptionCentering()"
		(selectionChange)="onSelectionChange($event.value)"
		(focus)="onFocus()"
		(blur)="onBlur()"
		(openedChange)="onOpenedChangeMerged($event)"
		class="jepto-select-element"
	>
		<mat-select-trigger>
			<ng-container *ngIf="triggerPreviewSwatches() as triggerSwatches">
				<div
					*ngIf="hasPrefix(); else triggerLabelOnly"
					class="jepto-ui-select__trigger-row jepto-ui-select__palette-trigger"
				>
					<mat-icon
						class="jepto-ui-select__trigger-prefix-icon material-symbols-outlined"
						aria-hidden="true"
						(click)="onPrefixIconClick()"
						>{{ prefixIcon() }}</mat-icon
					>
					<div
						*ngIf="triggerSwatches.length"
						class="jepto-ui-select__swatch-list jepto-ui-select__swatch-list--trigger jepto-ui-select__palette-trigger-swatches"
						aria-hidden="true"
					>
						<span
							*ngFor="let swatch of triggerSwatches"
							class="jepto-ui-select__trigger-swatch"
							[style.background-color]="swatch"
						></span>
					</div>
					<img
						*ngIf="selectedOptionLogoSrc() as logoSrc"
						class="jepto-ui-select__option-logo jepto-ui-select__trigger-logo"
						[src]="logoSrc"
						alt=""
						aria-hidden="true"
						loading="lazy"
					/>
					<span
						class="jepto-ui-select__trigger-label"
						[class.jepto-ui-select__trigger-label--placeholder]="
							triggerIsPlaceholder()
						"
						>{{ selectedDisplayLabel() }}</span
					>
				</div>
				<ng-template #triggerLabelOnly>
					<div
						class="jepto-ui-select__trigger-row jepto-ui-select__palette-trigger"
					>
						<div
							*ngIf="triggerSwatches.length"
							class="jepto-ui-select__swatch-list jepto-ui-select__swatch-list--trigger jepto-ui-select__palette-trigger-swatches"
							aria-hidden="true"
						>
							<span
								*ngFor="let swatch of triggerSwatches"
								class="jepto-ui-select__trigger-swatch"
								[style.background-color]="swatch"
							></span>
						</div>
						<img
							*ngIf="selectedOptionLogoSrc() as logoSrc"
							class="jepto-ui-select__option-logo jepto-ui-select__trigger-logo"
							[src]="logoSrc"
							alt=""
							aria-hidden="true"
							loading="lazy"
						/>
						<span
							class="jepto-ui-select__trigger-label"
							[class.jepto-ui-select__trigger-label--placeholder]="
								triggerIsPlaceholder()
							"
							>{{ selectedDisplayLabel() }}</span
						>
					</div>
				</ng-template>
			</ng-container>
		</mat-select-trigger>

		<mat-option
			*ngIf="panelSearch()"
			disabled
			class="jepto-ui-select__search-option"
		>
			<div
				class="jepto-ui-select__search-wrap"
				(click)="$event.stopPropagation()"
			>
				<span
					class="jepto-ui-select__search-glyph icon-search"
					aria-hidden="true"
				></span>
				<input
					type="text"
					class="jepto-ui-select__search-input"
					[placeholder]="panelSearchPlaceholder()"
					[attr.aria-label]="panelSearchPlaceholder()"
					(input)="onPanelSearchInput($event)"
					(click)="$event.stopPropagation()"
					(keydown)="onPanelSearchKeydown($event)"
				/>
			</div>
		</mat-option>

		<mat-option
			*ngIf="panelSearchEmpty()"
			disabled
			class="jepto-ui-select__empty-option"
		>
			No results
		</mat-option>

		<!-- Options from groups -->
		<ng-container *ngIf="hasGroups()">
			<mat-optgroup
				*ngFor="let group of groupsForPanel()"
				[label]="group.label"
				[disabled]="group.disabled"
			>
				<mat-option
					*ngFor="let option of group.options"
					[value]="option.value"
					[disabled]="option.disabled"
					class="jepto-select-option"
				>
					<span class="jepto-ui-select__option-row">
						<mat-icon
							*ngIf="option.icon"
							class="jepto-ui-select__option-icon material-symbols-outlined"
							aria-hidden="true"
							>{{ option.icon }}</mat-icon
						>
						<img
							*ngIf="getOptionLogoSrc(option) as logoSrc"
							class="jepto-ui-select__option-logo"
							[src]="logoSrc"
							alt=""
							aria-hidden="true"
							loading="lazy"
						/>
						<ng-container
							*ngIf="getPreviewSwatches(option) as optionSwatches"
						>
							<span
								*ngIf="optionSwatches.length"
								class="jepto-ui-select__swatch-list jepto-ui-select__swatch-list--option"
								aria-hidden="true"
							>
								<span
									*ngFor="let swatch of optionSwatches"
									class="jepto-ui-select__option-swatch"
									[style.background-color]="swatch"
								></span>
							</span>
						</ng-container>
						<span class="jepto-ui-select__option-label">{{
							option.label
						}}</span>
					</span>
				</mat-option>
			</mat-optgroup>
		</ng-container>

		<!-- Direct options -->
		<ng-container *ngIf="hasOptions() && !hasGroups()">
			<mat-option
				*ngFor="let option of optionsForPanel()"
				[value]="option.value"
				[disabled]="option.disabled"
				class="jepto-select-option"
			>
				<span class="jepto-ui-select__option-row">
					<mat-icon
						*ngIf="option.icon"
						class="jepto-ui-select__option-icon material-symbols-outlined"
						aria-hidden="true"
						>{{ option.icon }}</mat-icon
					>
					<img
						*ngIf="getOptionLogoSrc(option) as logoSrc"
						class="jepto-ui-select__option-logo"
						[src]="logoSrc"
						alt=""
						aria-hidden="true"
						loading="lazy"
					/>
					<ng-container
						*ngIf="getPreviewSwatches(option) as optionSwatches"
					>
						<span
							*ngIf="optionSwatches.length"
							class="jepto-ui-select__swatch-list jepto-ui-select__swatch-list--option"
							aria-hidden="true"
						>
							<span
								*ngFor="let swatch of optionSwatches"
								class="jepto-ui-select__option-swatch"
								[style.background-color]="swatch"
							></span>
						</span>
					</ng-container>
					<span class="jepto-ui-select__option-label">{{
						option.label
					}}</span>
				</span>
			</mat-option>
		</ng-container>
	</mat-select>

	<!-- Suffix Icons -->
	<div matSuffix *ngIf="hasSuffix()" class="suffix-container">
		<!-- Clear Button -->
		<button
			mat-icon-button
			*ngIf="showClear()"
			type="button"
			class="clear-button"
			tabindex="-1"
			(click)="onClearClick()"
			aria-label="Clear selection"
		>
			<mat-icon class="material-symbols-outlined">clear</mat-icon>
		</button>

		<!-- Custom Suffix Icon -->
		<mat-icon
			*ngIf="showCustomSuffixIcon()"
			class="suffix-icon material-symbols-outlined"
			(click)="onSuffixIconClick()"
		>
			{{ suffixIcon() }}
		</mat-icon>
	</div>

	<!-- Hint: lead text, optional link, optional end caption -->
	<mat-hint *ngIf="showHint()" class="jepto-ui-select__hint">
		<span class="jepto-ui-select__hint-main">
			<span *ngIf="hint().trim()" class="jepto-ui-select__hint-lead">{{
				hint()
			}}</span>
			<a
				*ngIf="hintLinkLabel().trim() && hintLinkHref().trim()"
				class="jepto-ui-select__hint-link"
				[href]="hintLinkHref()"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span
					class="jepto-ui-select__hint-external"
					aria-hidden="true"
				></span>
				{{ hintLinkLabel() }}
			</a>
			<button
				*ngIf="hintLinkLabel().trim() && !hintLinkHref().trim()"
				type="button"
				class="jepto-ui-select__hint-link"
				(click)="onHintLinkClick()"
			>
				<span
					class="jepto-ui-select__hint-external"
					aria-hidden="true"
				></span>
				{{ hintLinkLabel() }}
			</button>
		</span>
		<span *ngIf="hintEnd().trim()" class="jepto-ui-select__hint-end">{{
			hintEnd()
		}}</span>
	</mat-hint>

	<!-- Error Message -->
	<mat-error *ngIf="showError()">{{ errorMessage() }}</mat-error>
</mat-form-field>
